import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { loadLandingPage } from '@/state/buyer/landing-page/creator';
import { IState } from '@/state/IState';
import { setLandingPageData } from '@/state/buyer/landing-page/actions';
import { BuyerSession } from '@/server-events/BuyerSession';
import { setBuyerCommentThreads } from '@/state/buyer/commentThreads/actions';
import { setBuyerChangeRequest } from '@/state/buyer/changeRequests/actions';
import { BuyerChatSession, ThreadSession } from '@ygtechnologies/bygr-frontend-chat';
import { getChatConnectionManager } from '@/server-events/connections';
import { useAppDispatch } from '@/utils/hooks';

interface BuyerListenerProps {}

const BuyerListener: React.FC<BuyerListenerProps> = () => {
    const dispatch = useAppDispatch();
    const residentialUnitId = useSelector((state: IState) => state.buyer.permissions!.residentialUnitId);
    const activeProfile = useSelector((state: IState) => state.global.activeProfile);

    useEffect(() => {
        if (activeProfile && residentialUnitId) {
            const session = new BuyerSession(activeProfile.id, residentialUnitId);

            dispatch(loadLandingPage(null));

            session.addLandingPageReceiveHandler((data) => {
                dispatch(setLandingPageData(data));
            });

            session.addChangeRequestInfoReceiveHandler((data) => {
                dispatch(setBuyerChangeRequest(data));
            });

            return function cleanup() {
                session.dispose();
            };
        }
    }, [activeProfile, residentialUnitId, dispatch]);

    useEffect(() => {
        if (activeProfile && residentialUnitId) {
            const session = new BuyerChatSession(getChatConnectionManager(), activeProfile.id, residentialUnitId);

            session.addCommentThreadOverviewReceiveHandler((data) => {
                dispatch(setBuyerCommentThreads(data));
            });

            return function cleanup() {
                session.dispose();
            };
        }
    }, [activeProfile, residentialUnitId, dispatch]);

    const threads = useSelector((state: IState) => state.buyer.commentThreads);
    useEffect(() => {
        const session = new ThreadSession(getChatConnectionManager());

        session.addCommentThreadFlagChangedHandler((data) => {
            if (threads) {
                const newValue = threads.map((thread) => {
                    if (thread.threadId === data.threadId) {
                        return { ...thread, hasFlaggedMessages: data.flagged };
                    }

                    return thread;
                });
                dispatch(setBuyerCommentThreads(newValue));
            }
        });

        return function () {
            session.dispose();
        };
    }, [threads, dispatch]);

    return null;
};

export default BuyerListener;
